import React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DocumentPreview from "@/components/app/DocumentPreview";
import { qcStatusLabel, qcDocTypeLabel } from "@/lib/labels";
import { DownloadSimple, Sparkle, CheckCircle, XCircle, HandGrabbing, ArrowCounterClockwise, PencilSimple } from "@phosphor-icons/react";

export const STATUS_BADGE = {
  a_relire: { bg: "#FEF3C7", fg: "#92400E" },
  en_relecture: { bg: "#DBEAFE", fg: "#1E40AF" },
  valide: { bg: "#DCFCE7", fg: "#166534" },
  rejete: { bg: "#FEE2E2", fg: "#991B1B" },
};

const ACTION_LABELS = {
  creation: "Soumission",
  prise_en_charge: "Prise en charge",
  relecture_ia: "Relecture IA lancée",
  validation: "Validation",
  rejet: "Rejet",
  resoumission: "Resoumission",
  modification: "Contenu mis à jour",
};

function ChecklistTab({ review, editable, onChanged }) {
  const [items, setItems] = React.useState(review.checklist || []);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setItems(review.checklist || []); }, [review.id, review.updated_at, review.checklist]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/qc/reviews/${review.id}/checklist`, { items });
      toast.success("Checklist enregistrée");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="qc-checklist">
      {items.map((it) => (
        <div key={it.key} className="border border-zinc-200 rounded-md p-3 bg-white">
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={!!it.checked}
              disabled={!editable}
              onCheckedChange={(v) => setItems(items.map((x) => x.key === it.key ? { ...x, checked: !!v } : x))}
              data-testid={`qc-check-${it.key}`}
              className="mt-0.5"
            />
            <span>{it.label}</span>
          </label>
          <Input
            className="mt-2 text-xs"
            placeholder="Commentaire du relecteur (optionnel)"
            value={it.comment || ""}
            disabled={!editable}
            onChange={(e) => setItems(items.map((x) => x.key === it.key ? { ...x, comment: e.target.value } : x))}
            data-testid={`qc-check-comment-${it.key}`}
          />
        </div>
      ))}
      {editable && (
        <Button onClick={save} disabled={saving} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="qc-checklist-save">
          {saving ? "Enregistrement…" : "Enregistrer la checklist"}
        </Button>
      )}
      {!editable && <p className="text-xs text-zinc-500">Checklist modifiable par le relecteur pendant la relecture.</p>}
    </div>
  );
}

function CommentsTab({ review, onChanged }) {
  const [text, setText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/qc/reviews/${review.id}/comments`, { text });
      setText("");
      onChanged();
    } catch { toast.error("Commentaire non envoyé"); }
    finally { setSending(false); }
  };
  return (
    <div data-testid="qc-comments">
      <ul className="space-y-3 mb-4">
        {(review.comments || []).length === 0 && <li className="text-sm text-zinc-500">Aucun commentaire.</li>}
        {(review.comments || []).map((c) => (
          <li key={c.id} className="border border-zinc-200 bg-white rounded-md p-3" data-testid={`qc-comment-${c.id}`}>
            <div className="text-xs text-zinc-500 mb-1">{c.author_name} — {new Date(c.created_at).toLocaleString("fr-FR")}</div>
            <div className="text-sm whitespace-pre-wrap">{c.text}</div>
          </li>
        ))}
      </ul>
      <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ajouter un commentaire…" data-testid="qc-comment-input" />
      <div className="flex justify-end mt-2">
        <Button onClick={add} disabled={sending || !text.trim()} variant="outline" data-testid="qc-comment-add">Envoyer</Button>
      </div>
    </div>
  );
}

function HistoryTab({ review }) {
  return (
    <ul className="space-y-2" data-testid="qc-history">
      {(review.history || []).slice().reverse().map((h, i) => (
        <li key={`${h.at}-${i}`} className="flex gap-3 text-sm border-b border-zinc-100 pb-2">
          <span className="text-xs text-zinc-400 w-36 shrink-0">{new Date(h.at).toLocaleString("fr-FR")}</span>
          <span>
            <span className="font-medium">{ACTION_LABELS[h.action] || h.action}</span>
            <span className="text-zinc-500"> — {h.actor}{h.detail ? ` : ${h.detail}` : ""}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function AiTab({ review, isReviewer, onLaunch, launching }) {
  if (review.ai_status === "en_generation") {
    return (
      <div className="p-12 text-center text-sm text-zinc-500 border border-zinc-200 rounded-md bg-white" data-testid="qc-ai-generating">
        <span className="inline-block w-4 h-4 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mr-2 align-middle"></span>
        Relecture IA en cours — le rapport apparaîtra automatiquement…
      </div>
    );
  }
  if (review.ai_status === "erreur") {
    return (
      <div className="border border-red-200 bg-red-50 rounded-md p-6 text-sm text-red-800" data-testid="qc-ai-error">
        {review.ai_error || "La relecture IA a échoué."}
        {isReviewer && <Button variant="outline" size="sm" className="ml-3" onClick={onLaunch} disabled={launching} data-testid="qc-ai-retry">Relancer</Button>}
      </div>
    );
  }
  if (review.ai_report) {
    return <div data-testid="qc-ai-report"><DocumentPreview content={review.ai_report} /></div>;
  }
  return (
    <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center text-sm text-zinc-500" data-testid="qc-ai-empty">
      Aucune relecture IA pour l'instant.
      {isReviewer && (
        <div className="mt-3">
          <Button variant="outline" onClick={onLaunch} disabled={launching || !review.content_text} data-testid="qc-ai-launch">
            <Sparkle size={16} className="mr-1" /> Lancer la relecture IA
          </Button>
        </div>
      )}
    </div>
  );
}

function DecisionDialog({ decision, setDecision, review, onChanged }) {
  const [note, setNote] = React.useState("");
  const [sending, setSending] = React.useState(false);
  if (!decision) return null;
  const confirm = async () => {
    setSending(true);
    try {
      await api.post(`/qc/reviews/${review.id}/decision`, { decision, note: note || null });
      toast.success(decision === "valide" ? "Document validé" : "Document rejeté");
      setDecision(null);
      setNote("");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Décision impossible"); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={!!decision} onOpenChange={() => setDecision(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">{decision === "valide" ? "Valider le document" : "Rejeter le document"}</DialogTitle>
          <DialogDescription>La décision clôture la relecture en cours et est tracée dans l'historique du document.</DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder={decision === "valide" ? "Note de validation (optionnel)" : "Motif du rejet et corrections attendues"}
          data-testid="qc-decision-note" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setDecision(null)}>Annuler</Button>
          <Button onClick={confirm} disabled={sending} className={decision === "valide" ? "bg-green-700 hover:bg-green-800" : "bg-red-700 hover:bg-red-800"} data-testid="qc-decision-confirm">
            {sending ? "…" : decision === "valide" ? "Confirmer la validation" : "Confirmer le rejet"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Panneau de détail d'une relecture : document, relecture IA, checklist, commentaires, historique. */
export function QcDetail({ review, onChanged }) {
  const { user } = useAuth();
  const [decision, setDecision] = React.useState(null);
  const [launching, setLaunching] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [editText, setEditText] = React.useState("");

  if (!review) {
    return <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center text-zinc-500 text-sm" data-testid="qc-detail-empty">Sélectionnez une relecture dans la liste.</div>;
  }

  const isReviewer = ["admin", "expert"].includes(user?.role);
  const b = STATUS_BADGE[review.status] || STATUS_BADGE.a_relire;
  const canTake = isReviewer && review.status === "a_relire";
  const canDecide = isReviewer && review.status === "en_relecture";
  const canResubmit = review.status === "rejete";
  const canEdit = review.status === "a_relire" || review.status === "rejete";
  const checklistEditable = isReviewer && review.status === "en_relecture";

  const act = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action impossible"); }
  };

  const launchAi = async () => {
    setLaunching(true);
    await act(() => api.post(`/qc/reviews/${review.id}/ai-review`), "Relecture IA lancée");
    setLaunching(false);
  };

  const downloadFile = async () => {
    try {
      const res = await api.get(`/qc/reviews/${review.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = review.file_name || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  const saveContent = async () => {
    await act(() => api.put(`/qc/reviews/${review.id}`, { content_text: editText }), "Contenu mis à jour");
    setEditing(false);
  };

  return (
    <div data-testid="qc-detail">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{qcDocTypeLabel(review.doc_type)} • soumis par {review.submitted_by?.name}</div>
          <h2 className="font-serif-display text-2xl">{review.title}</h2>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: b.bg, color: b.fg }} data-testid="qc-status-badge">{qcStatusLabel(review.status)}</span>
            {review.reviewer && <span className="text-xs text-zinc-500">Relecteur : {review.reviewer.name}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canTake && (
            <Button onClick={() => act(() => api.post(`/qc/reviews/${review.id}/take`), "Relecture prise en charge")} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="qc-take-btn">
              <HandGrabbing size={16} className="mr-1" /> Prendre en charge
            </Button>
          )}
          {isReviewer && review.content_text && review.ai_status !== "en_generation" && (
            <Button variant="outline" onClick={launchAi} disabled={launching} data-testid="qc-ai-review-btn">
              <Sparkle size={16} className="mr-1" /> {review.ai_report ? "Relancer la relecture IA" : "Relecture IA"}
            </Button>
          )}
          {canDecide && (
            <>
              <Button onClick={() => setDecision("valide")} className="bg-green-700 hover:bg-green-800" data-testid="qc-validate-btn"><CheckCircle size={16} className="mr-1" /> Valider</Button>
              <Button onClick={() => setDecision("rejete")} className="bg-red-700 hover:bg-red-800" data-testid="qc-reject-btn"><XCircle size={16} className="mr-1" /> Rejeter</Button>
            </>
          )}
          {canResubmit && (
            <Button variant="outline" onClick={() => act(() => api.post(`/qc/reviews/${review.id}/resubmit`), "Document resoumis pour relecture")} data-testid="qc-resubmit-btn">
              <ArrowCounterClockwise size={16} className="mr-1" /> Resoumettre
            </Button>
          )}
        </div>
      </div>

      {review.status === "rejete" && review.decision_note && (
        <div className="border border-red-200 bg-red-50 rounded-md p-4 text-sm text-red-800 mb-3" data-testid="qc-rejection-note">
          <span className="font-medium">Motif du rejet :</span> {review.decision_note}
        </div>
      )}

      <Tabs defaultValue="document">
        <TabsList>
          <TabsTrigger value="document" data-testid="qc-tab-document">Document</TabsTrigger>
          <TabsTrigger value="ia" data-testid="qc-tab-ia">Relecture IA</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="qc-tab-checklist">Checklist CQ</TabsTrigger>
          <TabsTrigger value="commentaires" data-testid="qc-tab-comments">Commentaires ({(review.comments || []).length})</TabsTrigger>
          <TabsTrigger value="historique" data-testid="qc-tab-history">Historique</TabsTrigger>
        </TabsList>
        <TabsContent value="document">
          {review.file_path && (
            <div className="flex items-center gap-3 mb-3 border border-zinc-200 bg-white rounded-md p-3">
              <span className="text-sm truncate">{review.file_name}</span>
              <Button variant="outline" size="sm" onClick={downloadFile} data-testid="qc-download-file-btn"><DownloadSimple size={14} className="mr-1" /> Télécharger</Button>
            </div>
          )}
          {editing ? (
            <div>
              <Textarea rows={16} value={editText} onChange={(e) => setEditText(e.target.value)} data-testid="qc-edit-textarea" />
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" onClick={() => setEditing(false)}>Annuler</Button>
                <Button onClick={saveContent} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="qc-edit-save">Enregistrer</Button>
              </div>
            </div>
          ) : (
            <>
              {canEdit && review.content_text && (
                <div className="flex justify-end mb-2">
                  <Button variant="outline" size="sm" onClick={() => { setEditText(review.content_text); setEditing(true); }} data-testid="qc-edit-btn">
                    <PencilSimple size={14} className="mr-1" /> Corriger le texte
                  </Button>
                </div>
              )}
              {review.content_text
                ? <DocumentPreview content={review.content_text} />
                : <div className="border border-dashed border-zinc-300 rounded-md p-8 text-center text-sm text-zinc-500">Aucun texte extrait — consultez le fichier source.</div>}
            </>
          )}
        </TabsContent>
        <TabsContent value="ia"><AiTab review={review} isReviewer={isReviewer} onLaunch={launchAi} launching={launching} /></TabsContent>
        <TabsContent value="checklist"><ChecklistTab review={review} editable={checklistEditable} onChanged={onChanged} /></TabsContent>
        <TabsContent value="commentaires"><CommentsTab review={review} onChanged={onChanged} /></TabsContent>
        <TabsContent value="historique"><HistoryTab review={review} /></TabsContent>
      </Tabs>

      <DecisionDialog decision={decision} setDecision={setDecision} review={review} onChanged={onChanged} />
    </div>
  );
}
